import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Box
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import { useCurriculoActions } from '../../states/curriculo';

interface SalvarVersaoCurriculoModalProps {
    open: boolean;
    onClose: () => void;
    contatoId: number;
    curriculoData: any;
    onSaveSuccess?: () => void;
}

export const SalvarVersaoCurriculoModal: React.FC<SalvarVersaoCurriculoModalProps> = ({
    open,
    onClose,
    contatoId,
    curriculoData,
    onSaveSuccess
}) => {
    const curriloActions = useCurriculoActions();
    const [descricao, setDescricao] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const handleSave = async () => {
        // Validações
        if (!descricao.trim()) {
            setError('A descrição é obrigatória');
            return;
        }

        if (descricao.length > 255) {
            setError('A descrição deve ter no máximo 255 caracteres');
            return;
        }

        if (!curriculoData) {
            setError('Dados do currículo não encontrados');
            return;
        }

        setSaving(true);
        setError('');

        try {
            await curriloActions.salvarHistorico({
                idContato: contatoId,
                descricao: descricao.trim(),
                curriculoData: curriculoData
            });

            toast.success('Versão salva com sucesso!');
            setDescricao('');
            onClose();
            
            if (onSaveSuccess) {
                onSaveSuccess();
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Erro ao salvar versão';
            setError(errorMessage);
            toast.error(errorMessage);
            console.error('Erro ao salvar versão:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            setDescricao('');
            setError('');
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Salvar Versão do Currículo</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                <Alert severity="info" sx={{ mb: 2 }}>
                    Descreva o motivo ou contexto desta versão para facilitar a identificação futura.
                </Alert>

                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    rows={3}
                    label="Descrição da versão"
                    placeholder="Ex: Versão para vaga de Analista Sênior na Empresa X"
                    value={descricao}
                    onChange={(e) => {
                        setDescricao(e.target.value);
                        setError('');
                    }}
                    disabled={saving}
                    helperText={`${descricao.length}/255 caracteres`}
                    error={!!error || descricao.length > 255}
                    sx={{ mt: 2 }}
                />
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleClose}
                    disabled={saving}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={saving || !descricao.trim() || descricao.length > 255}
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                    {saving ? 'Salvando...' : 'Salvar Versão'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
